'use client';

import {
	createContext,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import useDriftClientIsReady from 'src/hooks/useDriftClientIsReady';
import useCurrentWalletConnectionState from 'src/hooks/useCurrentWalletConnectionState';
import useWalletAutoconnectPlan, {
	UseWalletAutoconnectPlan,
} from 'src/providers/app/AppInitialLoadProvider/hooks/useWalletAutoconnectPlan';
import { INITIAL_AUTOCONNECT_TIMEOUT_MS } from 'src/providers/app/AppInitialLoadProvider/consts';
import { WALLET_AUTOCONNECT_DELAY_MS } from 'src/hooks/wallets/consts';
import { WithChildren } from 'src/utils/UtilityTypes';

interface AppInitialLoadState extends UseWalletAutoconnectPlan {
	isInitialLoading: boolean;
}

const AppInitialLoadContext = createContext<AppInitialLoadState | null>(null);

/**
 * App is "initial-loading" when:
 * - Drift client isn't ready yet, OR
 * - Wallet is actively connecting, OR
 * - Wallet-adapter autoconnect may still run within its startup window, OR
 * - Privy native restore is still pending resolution.
 */
export default function AppInitialLoadProvider({ children }: WithChildren) {
	const driftReady = useDriftClientIsReady();
	const { instance: walletState } = useCurrentWalletConnectionState();
	const {
		matchingWallet,
		shouldAttemptWalletAdapterAutoconnect,
		shouldAttemptPrivyNativeAutoconnect,
		shouldAwaitWalletAdapterAutoconnect,
		shouldAwaitPrivyNativeAutoconnect,
	} = useWalletAutoconnectPlan();

	const [
		hasWalletAdapterAutoconnectWindowElapsed,
		setHasWalletAdapterAutoconnectWindowElapsed,
	] = useState(false);
	const [
		hasSettledWalletAdapterAutoconnect,
		setHasSettledWalletAdapterAutoconnect,
	] = useState(false);
	const [
		hasSettledPrivyNativeAutoconnect,
		setHasSettledPrivyNativeAutoconnect,
	] = useState(false);
	const [hasSettledInitialDriftReadiness, setHasSettledInitialDriftReadiness] =
		useState(false);
	const [hasAutoconnectTimeoutElapsed, setHasAutoconnectTimeoutElapsed] =
		useState(false);

	const hasObservedWalletAdapterAutoconnectCandidateRef = useRef(false);
	const hasObservedPrivyNativeAutoconnectCandidateRef = useRef(false);
	const isWalletAlreadyConnectingOrConnected =
		walletState.Connecting ||
		walletState.AdapterConnected ||
		walletState.FullyConnected;

	// Wallet-adapter autoconnect should only influence initial app startup once.
	useEffect(() => {
		if (hasSettledWalletAdapterAutoconnect) {
			return;
		}

		if (!shouldAwaitWalletAdapterAutoconnect) {
			// If a startup candidate existed and no longer exists, treat startup as settled.
			if (hasObservedWalletAdapterAutoconnectCandidateRef.current) {
				setHasSettledWalletAdapterAutoconnect(true);
			}
			return;
		}

		hasObservedWalletAdapterAutoconnectCandidateRef.current = true;

		if (
			hasWalletAdapterAutoconnectWindowElapsed ||
			// If we're already connecting/connected, no need to wait for an autoconnect window.
			isWalletAlreadyConnectingOrConnected
		) {
			setHasSettledWalletAdapterAutoconnect(true);
			return;
		}

		const timer = setTimeout(() => {
			setHasWalletAdapterAutoconnectWindowElapsed(true);
		}, WALLET_AUTOCONNECT_DELAY_MS);

		return () => {
			clearTimeout(timer);
		};
	}, [
		hasSettledWalletAdapterAutoconnect,
		isWalletAlreadyConnectingOrConnected,
		hasWalletAdapterAutoconnectWindowElapsed,
		shouldAwaitWalletAdapterAutoconnect,
	]);

	const awaitingWalletAdapterAutoconnect =
		!hasSettledWalletAdapterAutoconnect &&
		shouldAwaitWalletAdapterAutoconnect &&
		!isWalletAlreadyConnectingOrConnected &&
		!hasWalletAdapterAutoconnectWindowElapsed;

	// Drift client readiness is only a startup gate; don't re-open loading on later disconnects.
	useEffect(() => {
		if (!hasSettledInitialDriftReadiness && driftReady) {
			setHasSettledInitialDriftReadiness(true);
		}
	}, [driftReady, hasSettledInitialDriftReadiness]);

	const shouldAwaitInitialDriftReadiness =
		!hasSettledInitialDriftReadiness && !driftReady;

	const shouldAwaitPrivyNativeAutoconnectUntilConnected =
		shouldAwaitPrivyNativeAutoconnect &&
		!walletState.AdapterConnected &&
		!walletState.FullyConnected;

	// Privy native autoconnect should also only influence initial app startup once.
	useEffect(() => {
		if (hasSettledPrivyNativeAutoconnect) {
			return;
		}

		if (!shouldAwaitPrivyNativeAutoconnectUntilConnected) {
			if (hasObservedPrivyNativeAutoconnectCandidateRef.current) {
				setHasSettledPrivyNativeAutoconnect(true);
			}
			return;
		}

		hasObservedPrivyNativeAutoconnectCandidateRef.current = true;
	}, [
		hasSettledPrivyNativeAutoconnect,
		shouldAwaitPrivyNativeAutoconnectUntilConnected,
	]);

	// In case anything never resolves, set a hard timeout for entire autoconnect flow.
	useEffect(() => {
		const timer = setTimeout(() => {
			setHasAutoconnectTimeoutElapsed(true);
		}, INITIAL_AUTOCONNECT_TIMEOUT_MS);

		return () => {
			clearTimeout(timer);
		};
	}, []);

	const awaitingPrivyNativeAutoconnect =
		!hasSettledPrivyNativeAutoconnect &&
		shouldAwaitPrivyNativeAutoconnectUntilConnected;

	const awaitingAutoconnect =
		shouldAwaitInitialDriftReadiness ||
		walletState.Connecting ||
		awaitingWalletAdapterAutoconnect ||
		awaitingPrivyNativeAutoconnect;

	const isInitialLoading = !hasAutoconnectTimeoutElapsed && awaitingAutoconnect;

	const value = useMemo(
		() => ({
			isInitialLoading,
			matchingWallet,
			shouldAttemptWalletAdapterAutoconnect,
			shouldAttemptPrivyNativeAutoconnect,
			shouldAwaitWalletAdapterAutoconnect,
			shouldAwaitPrivyNativeAutoconnect,
		}),
		[
			isInitialLoading,
			matchingWallet,
			shouldAttemptWalletAdapterAutoconnect,
			shouldAttemptPrivyNativeAutoconnect,
			shouldAwaitWalletAdapterAutoconnect,
			shouldAwaitPrivyNativeAutoconnect,
		]
	);

	return (
		<AppInitialLoadContext.Provider value={value}>
			{children}
		</AppInitialLoadContext.Provider>
	);
}

/**
 * App is "initial-loading" when:
 * - Drift client isn't ready yet, OR
 * - Wallet is actively connecting, OR
 * - Wallet-adapter autoconnect may still run within its startup window, OR
 * - Privy native restore is still pending resolution.
 */
export function useAppInitialLoadContext(): AppInitialLoadState {
	const context = useContext(AppInitialLoadContext);

	if (!context) {
		throw new Error(
			'useAppInitialLoadContext must be used within AppInitialLoadProvider'
		);
	}

	return context;
}
